import React, { useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, LabelList } from "recharts";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1).replace(".", ",")} %`);
const pts = (d) => `${(Math.abs(d) * 100).toFixed(1).replace(".", ",")} pts`;

const mesKey = (e) => String(e.date_from || "").slice(0, 7);
const shift = (mes, n) => {
  const [y, m] = mes.split("-").map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};
const esParcial = (e) => {
  const a = String(e.date_from || ""), b = String(e.date_to || "");
  if (a.length < 10 || b.length < 10 || a.slice(0, 7) !== b.slice(0, 7)) return false;
  const fin = new Date(Number(a.slice(0, 4)), Number(a.slice(5, 7)), 0).getDate();
  return Number(a.slice(8, 10)) !== 1 || Number(b.slice(8, 10)) < fin;
};

function ratios(e) {
  const conReceta = (e.operaciones_solo_receta || 0) + (e.operaciones_mixtas || 0);
  return {
    ratio_mixtas: conReceta ? (e.operaciones_mixtas || 0) / conReceta : null,
    ratio_consejo: e.facturacion_libre ? (e.consejo_eur || 0) / e.facturacion_libre : (e.pct_consejo ?? null),
  };
}

function Delta({ d, texto, testid }) {
  if (d == null) return null;
  return (
    <div className={"text-xs font-semibold " + (d >= 0 ? "text-emerald-700" : "text-red-600")} data-testid={testid}>
      {d >= 0 ? "▲" : "▼"} {pts(d)} {texto}
    </div>
  );
}

function RatioChart({ data, dataKey, title, subtitle, color, testid, yoy, prevMes }) {
  const vals = data.map((d) => d[dataKey]).filter((v) => v != null);
  const media = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  const anio = yoy?.[dataKey];
  const prev = prevMes?.[dataKey];
  const last = data[data.length - 1]?.[dataKey];
  return (
    <div className="card p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Evolución mensual</div>
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl font-extrabold" style={{ color }} data-testid={`${testid}-last`}>{pct(last)}</div>
          <Delta d={last != null && prev != null ? last - prev : null} texto={`vs ${prevMes?.label}`} testid={`${testid}-var-mes`} />
          <Delta d={last != null && anio != null ? last - anio : null} texto={`vs ${yoy?.label}`} testid={`${testid}-var-anio`} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 20, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(1).replace(".", ",")}%`} domain={[0, "auto"]} />
          <Tooltip formatter={(v) => pct(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
          {media != null && <ReferenceLine y={media} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: `Media ${pct(media)}`, position: "insideTopLeft", fontSize: 10, fill: "#64748b" }} />}
          <Line type="monotone" dataKey={dataKey} name={title} stroke={color} strokeWidth={2.5} dot={{ r: 4 }} connectNulls>
            <LabelList dataKey={dataKey} position="top" formatter={(v) => (v == null ? "" : `${(v * 100).toFixed(1).replace(".", ",")}%`)} style={{ fontSize: 10, fill: "#334155" }} />
          </Line>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RatiosEvolucion({ evolution, historico }) {
  const data = useMemo(() => evolution.map((e) => ({ label: esParcial(e) ? `${e.label}*` : e.label, ...ratios(e) })), [evolution]);
  const [yoy, prevMes] = useMemo(() => {
    const last = evolution[evolution.length - 1];
    if (!last) return [null, null];
    const find = (n) => {
      const ref = (historico || evolution).find((e) => mesKey(e) === shift(mesKey(last), n));
      return ref ? { label: ref.label, ...ratios(ref) } : null;
    };
    return [find(-12), find(-1)];
  }, [evolution, historico]);
  if (!data.length) return null;
  const hayParcial = evolution.some(esParcial);
  return (
    <div data-testid="ratios-evolucion">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RatioChart data={data} dataKey="ratio_mixtas" color="#1f6fd1" testid="ratio-mixtas-chart" yoy={yoy} prevMes={prevMes}
                    title="Ratio de ventas mixtas" subtitle="Operaciones con receta en las que además se vendió algo libre, sobre el total de operaciones con receta." />
        <RatioChart data={data} dataKey="ratio_consejo" color="#f59e0b" testid="ratio-consejo-chart" yoy={yoy} prevMes={prevMes}
                    title="Ratio de consejo" subtitle="Venta de consejo sobre la venta libre del mes." />
      </div>
      {hayParcial && <p className="text-xs text-slate-500 mt-2" data-testid="ratios-parcial-nota">* Mes parcial: el ratio se calcula solo con los días subidos, así que se puede comparar con meses completos.</p>}
    </div>
  );
}
